import { useEffect } from 'react';
import { createBrowserRouter, Navigate, Route, RouterProvider, Routes, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import TeacherLayout from './components/TeacherLayout';
import Loading from './components/Loading';
import Account from './routes/Account';
import AssignmentView from './routes/AssignmentView';
import Home from './routes/Home';
import Login from './routes/Login';
import NotFound from './routes/NotFound';
import ResetPassword from './routes/ResetPassword';
import CurrentAssignment from './routes/student/CurrentAssignment';
import Flashcards, { Vocabulary } from './routes/student/Flashcards';
import MyClasses from './routes/student/MyClasses';
import OceanPage from './routes/student/Ocean';
import { OceanGame, OceanGames } from './routes/student/Games';
import Shop from './routes/student/Shop';
import ClassGame from './routes/teacher/ClassGame';
import BugReports from './routes/teacher/BugReports';
import TeacherShop from './routes/teacher/TeacherShop';
import Work from './routes/student/Work';
import Assign from './routes/teacher/Assign';
import AquariumPage from './routes/teacher/AquariumPage';
import AddStudents from './routes/teacher/AddStudents';
import Assignments from './routes/teacher/Assignments';
import Billing from './routes/teacher/Billing';
import Library from './routes/teacher/Library';
import ProgressPage from './routes/teacher/Progress';
import Roster from './routes/teacher/Roster';
import StudentVocab from './routes/teacher/StudentVocab';
import { reloadIfNewBuild } from './lib/staleBuild';
import { ClassroomProvider, useClassroom } from './state/Classroom';

function Gate() {
  const { session, authReady, role } = useClassroom();
  const location = useLocation();
  // A newer version of the site is out: load it on the way to the next page.
  useEffect(() => { reloadIfNewBuild(); }, [location.pathname]);

  // Teachers sign in with email, so the reset link has to work signed-out.
  if (location.pathname === '/reset-password') return <ResetPassword />;
  if (!authReady) return null;
  if (!session) return <Login />;
  if (!role) return <div className="page"><Loading what="your account" /></div>;

  return (
    <Routes>
      <Route element={role === 'student' ? <Layout /> : <TeacherLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/a/:id" element={<AssignmentView />} />
        <Route path="/account" element={<Account />} />
        {role === 'student' ? (
          <>
            <Route path="/video" element={<CurrentAssignment kind="video" />} />
            <Route path="/story" element={<CurrentAssignment kind="story" />} />
            <Route path="/grammar" element={<CurrentAssignment kind="grammar" />} />
            <Route path="/work" element={<Work />} />
            <Route path="/cards" element={<Flashcards />} />
            <Route path="/vocab" element={<Vocabulary />} />
            <Route path="/ocean" element={<OceanGames />} />
            <Route path="/ocean/explore" element={<OceanPage />} />
            <Route path="/ocean/:game" element={<OceanGame />} />
            <Route path="/play" element={<Navigate to="/ocean/class-game" replace />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/aquarium" element={<Navigate to="/ocean" replace />} />
            <Route path="/classes" element={<MyClasses />} />
          </>
        ) : (
          <>
            <Route path="/assign" element={<Assign />} />
            <Route path="/assign/:id" element={<Assign />} />
            <Route path="/assignments" element={<Assignments />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/library" element={<Library />} />
            <Route path="/progress" element={<ProgressPage />} />
            <Route path="/roster" element={<Roster />} />
            <Route path="/roster/add" element={<AddStudents />} />
            <Route path="/students/:id" element={<StudentVocab />} />
            <Route path="/ocean" element={<AquariumPage />} />
            <Route path="/game" element={<ClassGame />} />
            <Route path="/bugs" element={<BugReports />} />
            <Route path="/shop" element={<TeacherShop />} />
            <Route path="/aquarium" element={<Navigate to="/ocean" replace />} />
          </>
        )}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

// A data router (rather than <BrowserRouter>) only so pages can use
// useBlocker — the story page asks a student who leaves mid-read to stay.
// Routing itself still happens in <Gate>'s <Routes>, under one splat route.
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <ClassroomProvider>
        <Gate />
      </ClassroomProvider>
    ),
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
